import React, { useState } from 'react';
import { Bot, Brain, Cpu, Sparkles, AlertTriangle } from 'lucide-react';
import { useAIProviders, useAIAnalyze, useAIDecisions } from '@/hooks/useApi';
import { Card, Badge, LoadingSpinner } from '@/components/UI';
import { formatCurrency, formatDateTime, confidenceLabel, recommendationColor, pnlColor } from '@/utils';

export function AIControlPage() {
  const { data: providers, isLoading: provLoading } = useAIProviders();
  const analyze = useAIAnalyze();
  const { data: decisions, isLoading: decLoading } = useAIDecisions();

  const [symbol, setSymbol] = useState('BTC/USDT');
  const [provider, setProvider] = useState('');
  const [analysis, setAnalysis] = useState<Record<string, unknown> | null>(null);
  const [analyzing, setAnalyzing] = useState(false);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      const result = await analyze.mutateAsync({ symbol, provider: provider || undefined });
      setAnalysis(result.data);
    } catch {
      // handled by mutation
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="space-y-6 animate-in">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-bold text-surface-100">AI Control</h1>
        <Badge variant="brand">Advisory Only</Badge>
      </div>

      {/* Advisory Notice */}
      <div className="p-4 rounded-lg bg-warning/10 border border-warning/20">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-warning">AI is Advisory Only</p>
            <p className="text-xs text-surface-400 mt-1">
              AI recommendations are for informational purposes only. The AI engine NEVER directly places trades.
              All trade decisions must pass through the risk engine and be explicitly confirmed.
            </p>
          </div>
        </div>
      </div>

      {/* Analysis Input */}
      <Card title="Request Analysis">
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="block text-xs font-medium text-surface-400 mb-1.5">Symbol</label>
            <input className="input" value={symbol} onChange={(e) => setSymbol(e.target.value)} placeholder="BTC/USDT" />
          </div>
          <div className="w-48">
            <label className="block text-xs font-medium text-surface-400 mb-1.5">Provider</label>
            <select className="select" value={provider} onChange={(e) => setProvider(e.target.value)}>
              <option value="">Auto (Best Available)</option>
              {(providers ?? []).map((p: { name: string; display_name: string }) => (
                <option key={p.name} value={p.name}>{p.display_name}</option>
              ))}
            </select>
          </div>
          <button onClick={handleAnalyze} disabled={analyzing} className="btn-primary">
            {analyzing ? (
              <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Analyzing...</>
            ) : (
              <><Sparkles className="w-4 h-4" /> Analyze</>
            )}
          </button>
        </div>
      </Card>

      {/* Analysis Result */}
      {analysis && (
        <Card title="Analysis Result">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-surface-400">Recommendation</p>
                <p className={`text-xl font-bold ${recommendationColor(String(analysis.recommendation ?? ''))}`}>
                  {String(analysis.recommendation ?? '').replace(/_/g, ' ').toUpperCase()}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-surface-400">Confidence</p>
                <p className="text-xl font-bold text-surface-100">
                  {((analysis.confidence as number ?? 0) * 100).toFixed(1)}%
                </p>
                <p className="text-xs text-surface-500">{confidenceLabel(analysis.confidence as number ?? 0)}</p>
              </div>
            </div>

            {analysis.reasoning ? (
              <div>
                <p className="text-xs font-medium text-surface-400 mb-1">Reasoning</p>
                <p className="text-sm text-surface-300 leading-relaxed">{String(analysis.reasoning)}</p>
              </div>
            ) : null}

            {Array.isArray(analysis.signals) && analysis.signals.length > 0 ? (
              <div>
                <p className="text-xs font-medium text-surface-400 mb-2">Strategy Signals</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {analysis.signals.map((sig: Record<string, unknown>, i: number) => (
                    <div key={i} className="p-2 rounded-lg bg-surface-800/50">
                      <p className="text-xs text-surface-300">{String(sig.strategy ?? '')}</p>
                      <p className={`text-sm font-medium ${sig.direction === 'bullish' ? 'text-profit' : sig.direction === 'bearish' ? 'text-loss' : 'text-warning'}`}>
                        {String(sig.direction ?? '').toUpperCase()}
                      </p>
                      <p className="text-xs text-surface-500">Signal: {Number(sig.signal).toFixed(2)} • Strength: {Number(sig.strength).toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </Card>
      )}

      {/* Providers */}
      <Card title="AI Providers">
        {provLoading ? (
          <LoadingSpinner />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {(providers ?? []).map((p: { name: string; display_name: string; is_available: boolean; model: string }) => (
              <div key={p.name} className="p-4 rounded-lg bg-surface-800/50 border border-surface-700">
                <div className="flex items-center gap-2 mb-2">
                  {p.name === 'ollama' ? <Cpu className="w-4 h-4 text-brand-400" /> :
                   p.name === 'local' ? <Brain className="w-4 h-4 text-brand-400" /> :
                   <Bot className="w-4 h-4 text-brand-400" />}
                  <span className="text-sm font-medium text-surface-200">{p.display_name}</span>
                </div>
                <p className="text-xs text-surface-500">{p.model}</p>
                <Badge variant={p.is_available ? 'profit' : 'loss'} className="mt-2">
                  {p.is_available ? 'Available' : 'Unavailable'}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Decision History */}
      <Card title="Decision History" subtitle="Recent AI advisory decisions">
        {decLoading ? (
          <LoadingSpinner />
        ) : (
          <div className="space-y-2">
            {(decisions ?? []).length === 0 ? (
              <p className="text-sm text-surface-500 text-center py-4">No AI decisions yet</p>
            ) : (
              (decisions ?? []).slice(0, 20).map((d: Record<string, unknown>, i: number) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-surface-800/50 last:border-0">
                  <div className="flex items-center gap-3">
                    <Badge variant={d.was_followed ? 'profit' : 'neutral'}>
                      {d.was_followed ? 'Followed' : 'Ignored'}
                    </Badge>
                    <div>
                      <p className="text-sm text-surface-200">{String(d.symbol)} • {String(d.provider)}</p>
                      <p className="text-xs text-surface-500">{String(d.recommendation)} • {Number(d.confidence).toFixed(1)}% confidence</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm ${pnlColor(Number(d.outcome_pnl ?? 0))}`}>
                      {d.outcome_pnl !== null ? formatCurrency(Number(d.outcome_pnl)) : 'Pending'}
                    </p>
                    <p className="text-[10px] text-surface-600">{formatDateTime(String(d.created_at))}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
